/**
 * useCollectionState Hook
 * 
 * Manages state for collection views including filtering, sorting, grouping,
 * selection, and view mode. Provides a unified API for all collection operations.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  type CollectionState,
  type CollectionActions,
  type ViewMode,
  type SortState,
  type SortDirection,
  type ActiveFilter,
  type GroupedItems,
  type SelectionState,
  type FacetDefinition,
  type SortOption,
  type GroupOption,
  getItemCountState,
} from './types';

// ============================================================================
// Hook Options
// ============================================================================

export interface UseCollectionStateOptions<T, SortField = string, FilterField = string, GroupField = string> {
  /** Initial items */
  items: T[];
  /** Extract ID from item */
  getId: (item: T) => string;
  
  // View
  defaultViewMode?: ViewMode;
  
  // Search
  searchFields?: (keyof T)[];
  
  // Filtering
  facets?: FacetDefinition<FilterField>[];
  
  // Sorting
  sortOptions?: SortOption<SortField>[];
  defaultSort?: SortState<SortField>;
  /** Custom sort function */
  sortFn?: (items: T[], sort: SortState<SortField>) => T[];
  
  // Grouping
  groupOptions?: GroupOption<GroupField>[];
  defaultGroupBy?: GroupField;
  /** Custom group function */
  groupFn?: (items: T[], field: GroupField) => GroupedItems<T>[];
  
  // Selection
  enableSelection?: boolean;
  
  // Callbacks
  onSelectionChange?: (selectedIds: string[]) => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useCollectionState<T, SortField = string, FilterField = string, GroupField = string>(
  options: UseCollectionStateOptions<T, SortField, FilterField, GroupField>
): [CollectionState<T, SortField, FilterField, GroupField>, CollectionActions<T, SortField, FilterField, GroupField>] {
  const {
    items: initialItems,
    getId,
    defaultViewMode = 'list',
    searchFields = [],
    facets = [],
    sortOptions = [],
    defaultSort,
    sortFn,
    groupOptions = [],
    defaultGroupBy,
    groupFn,
    onSelectionChange,
  } = options;

  // ============================================================================
  // State
  // ============================================================================
  
  const [items, setItems] = useState<T[]>(initialItems);
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter<FilterField>[]>([]);
  const [sort, setSort] = useState<SortState<SortField>>(
    defaultSort || { field: sortOptions[0]?.field as SortField, direction: 'asc' }
  );
  const [groupBy, setGroupBy] = useState<GroupField | null>(defaultGroupBy || null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

  // Sync items when the prop changes. The guard exists because parents often
  // pass a new array reference on every render (a useMemo with unstable deps);
  // re-syncing on reference alone churns for no reason.
  //
  // It used to compare ids ONLY — so a row edited in place, same id and a new
  // value, never reached the table. Every list built on this hook showed the
  // pre-edit value until something changed the id set or the page reloaded.
  // Found on the Planning Priorities grid: adopting an item's size updated the
  // detail panel and the header count while the row itself still displayed the
  // number that had just been replaced.
  //
  // So: same ids AND the same item objects means nothing changed. A genuine
  // edit produces a new object for that row, which syncs. Reference equality
  // per item is cheap and does not require knowing anything about T.
  const lastSyncedRef = useRef<T[] | null>(null);
  useEffect(() => {
    const last = lastSyncedRef.current;
    if (
      last !== null &&
      last.length === initialItems.length &&
      last.every((item, i) => item === initialItems[i] && getId(item) === getId(initialItems[i]))
    ) {
      return;
    }
    lastSyncedRef.current = initialItems;
    setItems(initialItems);
  }, [initialItems, getId]);

  // ============================================================================
  // Filtering Logic
  // ============================================================================
  
  const filteredItems = useMemo(() => {
    let result = [...items];
    
    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => {
        if (searchFields.length === 0) {
          // Search all string fields
          return Object.values(item as object).some(value =>
            typeof value === 'string' && value.toLowerCase().includes(query)
          );
        }
        return searchFields.some(field => {
          const value = item[field];
          return typeof value === 'string' && value.toLowerCase().includes(query);
        });
      });
    }
    
    // Apply facet filters
    for (const filter of activeFilters) {
      const facet = facets.find(f => f.key === filter.facetKey);
      if (!facet) continue;
      
      result = result.filter(item => {
        const itemValue = (item as Record<string, unknown>)[filter.facetKey as string];
        const filterValue = filter.value;
        
        switch (filter.operator) {
          case 'equals':
            return itemValue === filterValue;
          case 'not_equals':
            return itemValue !== filterValue;
          case 'contains':
            return String(itemValue).toLowerCase().includes(String(filterValue).toLowerCase());
          case 'not_contains':
            return !String(itemValue).toLowerCase().includes(String(filterValue).toLowerCase());
          case 'in':
            return Array.isArray(filterValue) && filterValue.includes(String(itemValue));
          case 'not_in':
            return Array.isArray(filterValue) && !filterValue.includes(String(itemValue));
          case 'is_empty':
            return !itemValue || (Array.isArray(itemValue) && itemValue.length === 0);
          case 'is_not_empty':
            return !!itemValue && (!Array.isArray(itemValue) || itemValue.length > 0);
          default:
            return true;
        }
      });
    }
    
    return result;
  }, [items, searchQuery, searchFields, activeFilters, facets]);

  // ============================================================================
  // Sorting Logic
  // ============================================================================
  
  const sortedItems = useMemo(() => {
    if (!sort.field) return filteredItems;
    
    if (sortFn) {
      return sortFn(filteredItems, sort);
    }
    
    return [...filteredItems].sort((a, b) => {
      const aValue = (a as Record<string, unknown>)[sort.field as string];
      const bValue = (b as Record<string, unknown>)[sort.field as string];
      
      let comparison = 0;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime();
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }
      
      return sort.direction === 'desc' ? -comparison : comparison;
    });
  }, [filteredItems, sort, sortFn]);

  // ============================================================================
  // Grouping Logic
  // ============================================================================
  
  const groupedItems = useMemo<GroupedItems<T>[] | null>(() => {
    if (!groupBy) return null;
    
    if (groupFn) {
      return groupFn(sortedItems, groupBy);
    }
    
    const groupOption = groupOptions.find(g => g.field === groupBy);
    const groups = new Map<string, T[]>();
    
    for (const item of sortedItems) {
      let key: string;
      
      if (groupOption?.getGroupKey) {
        key = groupOption.getGroupKey(item);
      } else {
        const value = (item as Record<string, unknown>)[groupBy as string];
        key = value != null ? String(value) : 'Uncategorized';
      }
      
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
    }
    
    return Array.from(groups.entries()).map(([key, groupItems]) => ({
      key,
      label: groupOption?.getGroupLabel ? groupOption.getGroupLabel(key) : key,
      items: groupItems,
      count: groupItems.length,
    }));
  }, [sortedItems, groupBy, groupOptions, groupFn]);

  // ============================================================================
  // Selection Logic
  // ============================================================================
  
  const selectionState = useMemo<SelectionState>(() => {
    const itemIds = new Set(items.map(getId));
    const validSelectedIds = new Set(
      Array.from(selectedIds).filter(id => itemIds.has(id))
    );
    
    return {
      selectedIds: validSelectedIds,
      lastSelectedId,
      isAllSelected: items.length > 0 && validSelectedIds.size === items.length,
      isIndeterminate: validSelectedIds.size > 0 && validSelectedIds.size < items.length,
    };
  }, [items, selectedIds, lastSelectedId, getId]);

  // Notify on selection change
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(Array.from(selectionState.selectedIds));
    }
  }, [selectionState.selectedIds, onSelectionChange]);

  // ============================================================================
  // Actions
  // ============================================================================
  
  const addFilter = useCallback((filter: Omit<ActiveFilter<FilterField>, 'id'>) => {
    const newFilter: ActiveFilter<FilterField> = {
      ...filter,
      id: `${String(filter.facetKey)}-${String(filter.value)}-${Date.now()}`,
    };
    setActiveFilters(prev => [...prev, newFilter]);
  }, []);

  const removeFilter = useCallback((filterId: string) => {
    setActiveFilters(prev => prev.filter(f => f.id !== filterId));
  }, []);

  const clearFilters = useCallback(() => {
    setActiveFilters([]);
    setSearchQuery('');
  }, []);

  const setSortField = useCallback((field: SortField, direction?: SortDirection) => {
    setSort(prev => ({
      field,
      direction: direction || (prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'),
    }));
  }, []);

  const toggleSortDirection = useCallback(() => {
    setSort(prev => ({
      ...prev,
      direction: prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  const expandAllGroups = useCallback(() => {
    if (groupedItems) {
      setExpandedGroups(new Set(groupedItems.map(g => g.key)));
    }
  }, [groupedItems]);

  const collapseAllGroups = useCallback(() => {
    setExpandedGroups(new Set());
  }, []);

  // Selection actions
  const select = useCallback((id: string) => {
    setSelectedIds(prev => new Set([...prev, id]));
    setLastSelectedId(id);
  }, []);

  const deselect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const toggle = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setLastSelectedId(id);
  }, []);

  const toggleRange = useCallback((id: string, shiftKey: boolean) => {
    if (!shiftKey || !lastSelectedId) {
      toggle(id);
      return;
    }
    
    // Find indices
    const itemIds = sortedItems.map(getId);
    const startIdx = itemIds.indexOf(lastSelectedId);
    const endIdx = itemIds.indexOf(id);
    
    if (startIdx === -1 || endIdx === -1) {
      toggle(id);
      return;
    }
    
    const minIdx = Math.min(startIdx, endIdx);
    const maxIdx = Math.max(startIdx, endIdx);
    const rangeIds = itemIds.slice(minIdx, maxIdx + 1);
    
    setSelectedIds(prev => new Set([...prev, ...rangeIds]));
    setLastSelectedId(id);
  }, [lastSelectedId, sortedItems, getId, toggle]);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(filteredItems.map(getId)));
  }, [filteredItems, getId]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
    setLastSelectedId(null);
  }, []);

  const setSelected = useCallback((ids: string[]) => {
    setSelectedIds(new Set(ids));
  }, []);

  // ============================================================================
  // Build State and Actions
  // ============================================================================
  
  const state: CollectionState<T, SortField, FilterField, GroupField> = {
    items,
    filteredItems,
    sortedItems,
    groupedItems,
    viewMode,
    searchQuery,
    activeFilters,
    sort,
    group: {
      field: groupBy,
      expandedGroups,
    },
    selection: selectionState,
    itemCount: items.length,
    filteredCount: filteredItems.length,
    itemCountState: getItemCountState(filteredItems.length),
  };

  const actions: CollectionActions<T, SortField, FilterField, GroupField> = {
    setViewMode,
    setSearchQuery,
    addFilter,
    removeFilter,
    clearFilters,
    setFilters: setActiveFilters,
    setSort: setSortField,
    toggleSortDirection,
    setGroupBy,
    toggleGroup,
    expandAllGroups,
    collapseAllGroups,
    selection: {
      select,
      deselect,
      toggle,
      toggleRange,
      selectAll,
      deselectAll,
      setSelected,
    },
    setItems,
  };

  return [state, actions];
}

export default useCollectionState;
